import { useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useChatAskStore, ChatAskMessage } from "@/zapi-frontend/stores/chatAskStore";
import { API_URL } from "@/zapi-frontend/shared/api/axios";

type SSEEvent =
  | { type: "start" }
  | { type: "function_call"; message_to_display: string }
  | { type: "function_call_done"; message_to_display: string }
  | { type: "content"; content: string }
  | { type: "finish" };

export function useChatTest() {
  const { id: projectId } = useParams() as { id: string };
  const abortRef = useRef<AbortController | null>(null);

  const {
    getChatHistory,
    addMessage,
    clearHistory,
    getStreamState,
    setStreamState,
    appendContent,
    setStatusMessage,
    resetStreamState
  } = useChatAskStore();

  const messages = getChatHistory(projectId);
  const streamState = getStreamState(projectId);

  const handleSubmit = useCallback(
    async (messageText: string) => {
      if (!projectId || !messageText.trim()) return;

      const userMessage: ChatAskMessage = {
        role: "user",
        content: messageText.trim(),
        id: `user-${Date.now()}`
      };
      addMessage(projectId, userMessage);

      setStreamState(projectId, {
        isStreaming: true,
        currentContent: "",
        statusMessage: null,
        phase: "streaming"
      });

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const history = getChatHistory(projectId);
        const chatHistory = history
          .filter((m) => m.id !== userMessage.id)
          .slice(-20)
          .map((m) => ({ role: m.role, content: m.content }));

        const response = await fetch(`${API_URL}/chat-test`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            projectId: Number(projectId),
            message: messageText.trim(),
            chatHistory
          }),
          signal: controller.signal
        });

        if (!response.ok || !response.body) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulated = "";

        // limiting the number of iterations to avoid infinite loop
        for (let i = 0; i < 100; i++) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith("data: ")) continue;
            const jsonStr = trimmed.slice(6);
            if (!jsonStr) continue;

            let event: SSEEvent;
            try {
              event = JSON.parse(jsonStr);
            } catch {
              continue;
            }

            switch (event.type) {
              case "start":
                break;
              case "function_call":
                setStatusMessage(projectId, event.message_to_display);
                break;
              case "function_call_done":
                setStatusMessage(projectId, event.message_to_display);
                break;
              case "content":
                accumulated += event.content;
                appendContent(projectId, event.content);
                break;
              case "finish": {
                const assistantMessage: ChatAskMessage = {
                  role: "assistant",
                  content: accumulated,
                  id: `assistant-${Date.now()}`
                };
                addMessage(projectId, assistantMessage);
                resetStreamState(projectId);
                break;
              }
            }
          }
        }
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") {
          resetStreamState(projectId);
          return;
        }
        console.error("[chat-test] Stream error:", error);
        const errorMessage: ChatAskMessage = {
          role: "assistant",
          content: "Sorry, an error occurred. Please try again.",
          id: `error-${Date.now()}`
        };
        addMessage(projectId, errorMessage);
        resetStreamState(projectId);
      } finally {
        abortRef.current = null;
      }
    },
    [projectId, getChatHistory, addMessage, setStreamState, appendContent, setStatusMessage, resetStreamState]
  );

  const handleAbort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleClearChat = useCallback(() => {
    clearHistory(projectId);
    resetStreamState(projectId);
  }, [projectId, clearHistory, resetStreamState]);

  return {
    projectId,
    messages,
    streamState,
    handleSubmit,
    handleAbort,
    handleClearChat,
    hasMessages: messages.length > 0
  };
}
